import { useCallback, useState } from "react";

const MAX_FALLBACK_TILES = 300;

/**
 * Keeps the tiles that were just replaced until all new tiles are loaded, so that no holes
 * appear while the camera moves.
 *
 * @param tiles The tiles to show
 * @returns The replaced tiles to draw behind the new ones, and a callback for tiles that finished loading
 */
export function useFallbackTiles<T extends { key: string }>(
  tiles: T[],
): {
  fallbackTiles: T[];
  onTileSettled(key: string, isSettled: boolean): void;
} {
  const [settled, setSettled] = useState<ReadonlySet<string>>(new Set());
  const [shown, setShown] = useState({ tiles, fallbackTiles: new Array<T>() });

  // Derived while rendering, so that replaced tiles stay mounted and never disappear for a frame
  let { fallbackTiles } = shown;
  if (shown.tiles !== tiles) {
    const keys = new Set(tiles.map((tile) => tile.key));
    const replaced = [...shown.fallbackTiles, ...shown.tiles].filter(
      (tile) => !keys.has(tile.key) && settled.has(tile.key),
    );
    fallbackTiles = [
      ...new Map(replaced.map((tile) => [tile.key, tile])).values(),
    ].slice(-MAX_FALLBACK_TILES);
    setShown({ tiles, fallbackTiles });
  } else if (
    fallbackTiles.length > 0 &&
    tiles.every((tile) => settled.has(tile.key))
  ) {
    fallbackTiles = [];
    setShown({ tiles, fallbackTiles });
  }

  const onTileSettled = useCallback((key: string, isSettled: boolean) => {
    setSettled((previous) => {
      if (previous.has(key) === isSettled) return previous;
      const next = new Set(previous);
      if (isSettled) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  return { fallbackTiles, onTileSettled };
}
